import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { StripeBadge } from "@/components/StripeBadge";
import { PublicMobileNav, PUBLIC_LINKS } from "@/components/PublicMobileNav";

const links = PUBLIC_LINKS;

export const PublicFooter = () => (
  <footer className="border-t border-border py-10 mt-4">
    <div className="max-w-6xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-6 w-auto" />
        <span className="text-xs text-muted-foreground">Automatic quotations for contracting and procurement.</span>
      </div>
      <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-semibold">
        <Link data-testid="footer-how-it-works" to="/how-it-works" className="text-muted-foreground hover:text-foreground transition-colors">How it works</Link>
        <Link data-testid="footer-pricing" to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
        <Link data-testid="footer-for-vendors" to="/for-vendors" className="text-muted-foreground hover:text-foreground transition-colors">For vendors</Link>
        <Link data-testid="footer-faq" to="/faq" className="text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
        <Link data-testid="footer-terms" to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
        <Link data-testid="footer-privacy" to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
        <a data-testid="footer-contact" href="mailto:admin@matiqs.net" className="text-brand hover:underline">admin@matiqs.net</a>
        <Link data-testid="footer-signin" to="/login" className="text-primary hover:underline">Sign in</Link>
      </nav>
    </div>
    <div className="max-w-6xl mx-auto px-5 mt-6 flex justify-center md:justify-end">
      <StripeBadge testid="footer-stripe-badge" />
    </div>
  </footer>
);

export default function PublicShell({ children, eyebrow, title, intro, updated }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const appHome = user?.account_type === "contractor" ? "/marketplace" : user?.account_type === "einvoice" ? "/einvoicing/app" : "/dashboard";
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="public-brand" className="flex items-center gap-3 shrink-0">
            <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${pathname === l.to ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid="public-signin-button" variant="outline"
                    onClick={() => navigate(user ? appHome : "/login")}
                    className="hidden md:flex h-10 font-semibold">
              {user ? "My dashboard" : "Sign in"}
            </Button>
            <PublicMobileNav />
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="relative border-b border-border overflow-hidden">
          <div className="absolute inset-0 grid-fade pointer-events-none" />
          <div className="relative max-w-4xl mx-auto px-5 py-14 md:py-16">
            {eyebrow && (
              <span className="inline-block px-3 py-1 rounded-full bg-brand-soft text-brand text-[11px] font-bold uppercase tracking-[0.16em] border border-brand/20">
                {eyebrow}
              </span>
            )}
            <h1 data-testid="page-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-5">
              {title}
            </h1>
            {intro && <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl">{intro}</p>}
            {updated && <p className="text-xs text-muted-foreground mt-5">Last updated {updated}</p>}
          </div>
        </section>
        <div className="max-w-4xl mx-auto px-5 py-12">{children}</div>
      </main>

      <PublicFooter />
    </div>
  );
}

export const LegalSection = ({ n, title, children }) => (
  <section data-testid={`legal-section-${n}`} className="mb-9">
    <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100 mb-2.5">
      <span className="text-brand font-mono mr-2">{n}.</span>{title}
    </h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
  </section>
);
